import axios, { AxiosResponse } from "axios"
import { checkResponseStatus } from "@api/Utils"
import { NotFoundItemInArray } from '../../../src/interfaces/Items'
import { EntityId, EntityListResponseItem, EntityListResponseItemSummary, EntityListResponseItemVisibilityAdjustmentViewSummary, EntityListResponseObject, EntityListResponseString, EntitySingleResponseItem, EntitySingleResponseItemShopsAvailability, Item } from 'cs.node.utils/csapi/provisioning'
import { ItemsAnalysis } from "cs.node.utils/ItemUtils"
import { EntityListResponseItemAvailabilityAdjustmentViewSummary, EntityListResponseWarehouseMovementViewSummary, EntitySingleResponseItemMetadata, EntitySingleResponseListItemBusinessCondition } from "cs.node.utils/csapi/provisioning"

type CodeType = "BARCODE" | "BARCODES" | "EAN" | "ITEM_ID" | "MNF_CODE" | "MPN" | "NS_INTERNAL_ID" | "SKU" | "UPC" | undefined

export const getItemsMetadataFields = async() =>{
    try {
        const response:AxiosResponse<EntityListResponseString,any> = await axios.get(`api/items/metadata`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemsMetadata = async (field:string):Promise<EntityListResponseObject> =>{
    try {
        const response:AxiosResponse<EntityListResponseObject,any> = await axios.get(`api/items/metadata/${field}`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}


export interface MOCKCatalogItem {
    mnf_code?: string,
    brand?: string,
    sku_parents?: MOCKCatalogItem[],
    images?: string[],
    sku_parent?: string,
    skus?: MOCKCatalogItem[],
    sku?: string,
    name?: string,
    description?: string,
    size?: string
}

export const getItems = async(controller: AbortController, pageIndex?: number, pageSize?: number, sort?: string, searchTerm?: string)=>{
    try {
        const response:AxiosResponse<EntityListResponseItem,any> = await axios.get(`api/items`, {
            params: { pageIndex, pageSize, sort, searchTerm },
            signal: controller.signal
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemsFromCodeList = async (codeList: string[], type: string) =>{
    try {
        const response:AxiosResponse<EntityListResponseItemSummary,any> = await axios.post(`api/items/listbyCodeList?type=${type}`, codeList)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemsbyParentSku = async (skuParent: string) =>{
    try {
        const response:AxiosResponse<EntityListResponseItem, any> =  await axios.get(`api/items/getItemsByParentSKU/${skuParent}`)
        return response.data

    } catch (error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemShops = async (id: string) =>{
    try {
        const response:AxiosResponse<EntitySingleResponseItemShopsAvailability, any> =  await axios.get(`api/items/${id}/shops`)
        return response.data

    } catch (error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemBySku = async (sku: string) =>{
    try {
        const response:AxiosResponse<EntitySingleResponseItem, any> =  await axios.get(`api/items/getItemBySKU/${sku}`)
        return response.data

    } catch (error: any) {
        checkResponseStatus(error)
        throw error
    }

}

export const getItemById = async(id: string)=>{
    try {
        const response:AxiosResponse<EntitySingleResponseItem, any> =  await axios.get(`api/items/getItemById/${id}`)
        return response.data

    } catch (error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemsByIdList = async(idList: string[])=>{
    try {
        const response:AxiosResponse<Array<Item | NotFoundItemInArray>, any> =  await axios.post(`api/items/getItemsByIdList`, idList)
        return response.data

    } catch (error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const analyzeItems = async(items: Item[])=>{
    try {
        const response:AxiosResponse<ItemsAnalysis, any> =  await axios.post(`api/items/analyze`, {items})
        return response.data
    } catch (error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const patchItems = async(ids: string[], body: Item)=>{
    try {
        const response:AxiosResponse<PromiseSettledResult<Item>[], any> =  await axios.patch(`api/items/bulk`, {ids, body})
        return response.data
    } catch (error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const addImageToItems = async(data:FormData) =>{
    try {
        const response = await axios.post('api/items/image', data)
        return response.data
    } catch(error:any) {
        throw error
    }
}

export const getItemMetadata = async(id: string)=>{
    try {
        const response:AxiosResponse<EntitySingleResponseItemMetadata, any> =  await axios.get(`api/items/${id}/metadata`)
        return response.data

    } catch (error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemAvailabilityAdjustments = async(controller: AbortController, itemId: string, shopId?: string, dateFrom?: string, dateTo?: string, pageIndex?: number, pageSize?: number, sort?: string) =>{
    try {
        const params = {
            shopId,
            pageIndex,
            pageSize,
            sort,
            dateFrom,
            dateTo
        }

        const response: AxiosResponse<EntityListResponseItemAvailabilityAdjustmentViewSummary,any> = await axios.get(`api/items/${itemId}/availability-adjustments`, {
            params,
            signal: controller.signal
        })
        
        return response.data

    } catch (error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemBusinessConditions = async(itemId: string) =>{
    try{
        const response: AxiosResponse<EntitySingleResponseListItemBusinessCondition,any> = await axios.get(`api/items/${itemId}/business-conditions`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemWarehousesMovements = async(itemId: string, dateFrom?: string, dateTo?: string, pageIndex?: number, pageSize?: number, sort?: string, controller?: AbortController) =>{
    try {
        const params = {
            pageIndex,
            pageSize,
            sort,
            dateFrom,
            dateTo
        }

        const response: AxiosResponse<EntityListResponseWarehouseMovementViewSummary,any> = await axios.get(`api/items/${itemId}/warehouses-movements`, {
            params,
            signal: controller?.signal
        })
        
        return response.data

    } catch (error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const createItem = async(mnf_code: string, sku_parent: string, sku: string, inheritProperties: boolean) =>{
    try{
        const response: AxiosResponse<EntitySingleResponseItem,any> = await axios.post(`api/items`, {
            mnf_code, sku_parent, sku, inheritProperties
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const createItems = async(items:Item[]) =>{
    try{
        const response: AxiosResponse<PromiseSettledResult<Item>[],any> = await axios.post(`api/items/bulk`, {
            items
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const synchronizeParentItems = async() =>{
    try{
        const response = await axios.post(`api/items/synchronize-parent-items`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemVisibilityAdjustments = async(itemId: string, shop_id?: string, customer_id?: string) =>{
    try {
        const response: AxiosResponse<EntityListResponseItemVisibilityAdjustmentViewSummary,any> = await axios.post(`api/items/${itemId}/visibility-adjustments`, { shop_id, customer_id })
        return response.data
    } catch (error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemVisibilityAdjustmentsByShop = async(itemId: string, shopId: string) =>{
    try {
        const response: AxiosResponse<EntityListResponseItemVisibilityAdjustmentViewSummary,any> = await axios.get(`api/items/${itemId}/visibility-adjustments-by-shop`, {
            params: { shopId }
        })
        return response.data
    } catch (error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getItemVisibilityAdjustmentsByShopList = async(itemId: string, shopIdList: EntityId[]) =>{
    try {
        const response: AxiosResponse<EntityListResponseItemVisibilityAdjustmentViewSummary,any> = await axios.post(`api/items/${itemId}/visibility-adjustments-by-shop-list`, { shopIdList })
        return response.data
    } catch (error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const findItemByCode = async(code: string, type: CodeType) =>{
    try {
        const response: AxiosResponse<EntitySingleResponseItem,any> = await axios.get(`api/items/findByCode`, {
            params: { code, type }
        })
        return response.data
    } catch (error:any) {
        checkResponseStatus(error)
        throw error
    }
}